import { CantonPrices } from '@/blocks/PriceCalculator/prices'

export type Category =
  | 'strassenverkehrsamt'
  | 'fahrschule'
  | 'kurse'
  | 'fahrstunden'

export type StepId =
  | 'nothelferkurs'
  | 'gesuch'
  | 'theorie'
  | 'vku'
  | 'fahrlektionen'
  | 'pruefung'
  | 'wab'

export type Row = {
  label: string
  amount: number
  category: Category
  note?: string
  info?: string
}

export type Step = {
  id: StepId
  index: number
  title: string
  rows: Row[]
  total: number
  hideRows?: boolean
  custom?: 'lessons'
}

const RECOMMEND_BASE = 17
const RECOMMEND_PER_POINT = 14 / 6
const RECOMMEND_MIN = 3
const RECOMMEND_MAX = 45
export const ASSESSMENT_NEUTRAL = 3

export const LESSON_SLIDER_MIN = 0
export const LESSON_SLIDER_MAX = 50

export function recommendLessons(
  knowledge: number,
  privatePractice: number,
  talent: number
): number {
  const combined = knowledge + privatePractice + talent
  const neutral = ASSESSMENT_NEUTRAL * 3
  const recommended =
    RECOMMEND_BASE - RECOMMEND_PER_POINT * (combined - neutral)

  return Math.min(
    RECOMMEND_MAX,
    Math.max(RECOMMEND_MIN, Math.round(recommended))
  )
}

export type LessonPacking = {
  paket10: number
  paket5: number
  einzel: number
  cost: number
}

export function packLessons(
  count: number,
  prices: CantonPrices
): LessonPacking {
  const paket10 = Math.floor(count / 10)
  const remainder = count % 10

  const paket5 =
    remainder >= 5 && prices.lessonPackage5 < 5 * prices.singleLesson ? 1 : 0
  const einzel = remainder - paket5 * 5

  return {
    paket10,
    paket5,
    einzel,
    cost:
      paket10 * prices.lessonPackage10 +
      paket5 * prices.lessonPackage5 +
      einzel * prices.singleLesson,
  }
}

export type LessonBreakdownRow = {
  id: 'paket10' | 'paket5' | 'einzel'
  count: number
  label: string
  amount: number
}

export function lessonBreakdownRows(
  packing: LessonPacking,
  prices: CantonPrices
): LessonBreakdownRow[] {
  const rows: LessonBreakdownRow[] = []

  if (packing.paket10 > 0) {
    rows.push({
      id: 'paket10',
      count: packing.paket10,
      label: '10er Paket',
      amount: packing.paket10 * prices.lessonPackage10,
    })
  }
  if (packing.paket5 > 0) {
    rows.push({
      id: 'paket5',
      count: packing.paket5,
      label: '5er Paket',
      amount: packing.paket5 * prices.lessonPackage5,
    })
  }
  if (packing.einzel > 0) {
    rows.push({
      id: 'einzel',
      count: packing.einzel,
      label: 'Einzellektion',
      amount: packing.einzel * prices.singleLesson,
    })
  }

  return rows
}

export const ADMIN_FEE = 90

export const LICENSE_COMPLETED_STEPS: StepId[] = ['nothelferkurs', 'vku']

export function buildSteps(prices: CantonPrices, lessons: number): Step[] {
  const packing = packLessons(lessons, prices)
  const lessonCost = packing.cost

  const step = (
    id: StepId,
    index: number,
    title: string,
    rows: Row[],
    extra: Partial<Step> = {}
  ): Step => ({
    id,
    index,
    title,
    rows,
    total: rows.reduce((sum, row) => sum + row.amount, 0),
    ...extra,
  })

  return [
    step(
      'nothelferkurs',
      1,
      'Nothelferkurs',
      [
        {
          label: 'Nothelferkurs',
          amount: prices.firstaidCourse,
          category: 'kurse',
        },
      ],
      { hideRows: true }
    ),
    step('gesuch', 2, 'Gesuch Lernfahrausweis', [
      {
        label: 'Sehtest bei VIU (exklusiv bei BLINK)',
        amount: 0,
        category: 'fahrschule',
        note: 'kostenlos',
      },
      {
        label: 'Gesuch Lernfahrausweis',
        amount: prices.lfaApplication,
        category: 'strassenverkehrsamt',
      },
    ]),
    step('theorie', 3, 'Theorie & Lernfahrausweis', [
      {
        label: 'Anerkannte Theorie App',
        amount: prices.blinkTheoryApp,
        category: 'fahrschule',
      },
      {
        label: 'Theorieprüfung',
        amount: prices.theoryExam,
        category: 'strassenverkehrsamt',
      },
      {
        label: 'Ausstellung Lernfahrausweis',
        amount: prices.lfaIssuance,
        category: 'strassenverkehrsamt',
      },
    ]),
    step(
      'vku',
      4,
      'VKU',
      [
        {
          label: 'VKU',
          amount: prices.vkuCourse,
          category: 'kurse',
        },
      ],
      { hideRows: true }
    ),
    step(
      'fahrlektionen',
      5,
      'Fahrlektionen',
      [
        { label: 'Fahrlektionen', amount: lessonCost, category: 'fahrstunden' },
        {
          label: 'Adminpauschale bei BLINK',
          amount: ADMIN_FEE,
          category: 'fahrschule',
        },
      ],
      { custom: 'lessons' }
    ),
    step('pruefung', 6, 'Praktische Fahrprüfung', [
      {
        label: 'Prüfungspauschale bei BLINK',
        amount: prices.blinkExamFee,
        category: 'fahrschule',
        info: 'Pauschale für Prüfungsfahrzeug und Begleitung durch die Fahrschule.',
      },
      {
        label: 'Prüfungsgebühr Strassenverkehrsamt',
        amount: prices.examFee,
        category: 'strassenverkehrsamt',
      },
    ]),
    step('wab', 7, 'WAB Kurs', [
      {
        label: 'WAB Kurs beim TCS mit BLINK Rabatt',
        amount: prices.wabCourse,
        category: 'kurse',
        info: 'Weiterausbildungskurs, den du nach der Prüfung absolvierst.',
      },
    ]),
  ]
}

export type Result = Record<Category, number> & { total: number }

export const RESULT_ROWS: { category: Category; label: string }[] = [
  { category: 'strassenverkehrsamt', label: 'Gebühren Strassenverkehrsamt' },
  { category: 'fahrschule', label: 'Gebühren Fahrschule' },
  { category: 'kurse', label: 'Kurse' },
  { category: 'fahrstunden', label: 'Fahrstunden' },
]

export function computeResult(steps: Step[], doneIds: Set<StepId>): Result {
  const result: Result = {
    strassenverkehrsamt: 0,
    fahrschule: 0,
    kurse: 0,
    fahrstunden: 0,
    total: 0,
  }

  for (const step of steps) {
    if (doneIds.has(step.id)) continue
    for (const row of step.rows) {
      result[row.category] += row.amount
      result.total += row.amount
    }
  }

  return result
}

export function formatChf(amount: number): string {
  const rounded = Math.round(amount)
  const grouped = String(Math.abs(rounded)).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    '’'
  )
  return `CHF ${rounded < 0 ? '-' : ''}${grouped}`
}
